// Social post view tracking: visibility + dwell detection, de-duplicated, batched,
// and server-authoritative. See /app/memory for the feature contract.
import { API } from "@/lib/api";

// ---- Tunable thresholds (change these two to adjust what counts as a view) ----
export const VIEW_VISIBILITY_RATIO = 0.5; // fraction of the post that must be on screen
export const VIEW_DWELL_MS = 1000;         // how long it must stay visible (ms)
// ---- Reliability knob ----
export const VIEW_BATCH_INTERVAL_MS = 3000; // how often queued views are flushed

// Stable anonymous identity for logged-out guests (persists across sessions on device).
function anonId() {
  let id = localStorage.getItem("rc_anon_id");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) ||
      `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("rc_anon_id", id);
  }
  return id;
}

const pending = new Set();          // post ids waiting to be sent
const enqueuedEver = new Set();     // avoid re-queuing within this tab session
let timer = null;

function payload(ids) {
  return {
    post_ids: ids,
    anon_id: anonId(),
    token: localStorage.getItem("rc_token") || undefined, // lets sendBeacon keep identity
  };
}

async function flush() {
  if (pending.size === 0) return;
  const ids = [...pending];
  pending.clear();
  try {
    await fetch(`${API}/social/views`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload(ids)),
      keepalive: true,
    });
  } catch {
    // network hiccup — requeue so a later flush retries (server dedupes anyway)
    ids.forEach((id) => pending.add(id));
  }
}

// Best-effort flush when the user leaves/backgrounds the page or closes the app.
function flushBeacon() {
  if (pending.size === 0) return;
  const ids = [...pending];
  pending.clear();
  try {
    const blob = new Blob([JSON.stringify(payload(ids))], { type: "application/json" });
    const ok = navigator.sendBeacon && navigator.sendBeacon(`${API}/social/views`, blob);
    if (!ok) ids.forEach((id) => pending.add(id));
  } catch {
    ids.forEach((id) => pending.add(id));
  }
}

let listenersBound = false;
function ensureStarted() {
  if (timer) return;
  timer = setInterval(flush, VIEW_BATCH_INTERVAL_MS);
  if (!listenersBound) {
    listenersBound = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushBeacon();
    });
    window.addEventListener("pagehide", flushBeacon);
    window.addEventListener("beforeunload", flushBeacon);
  }
}

// Queue one post view (call once the visibility+dwell criteria are met).
export function enqueueView(postId) {
  if (!postId || enqueuedEver.has(postId)) return;
  enqueuedEver.add(postId);
  pending.add(postId);
  ensureStarted();
}
